import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { StringKey } from '@ipfabric/utils/types/StringKey';
import { isNullable } from '@ipfabric/utils/types/isNullable';
import classNames from 'classnames';
import { FormField } from 'components/FormField/FormField';
import {
  TextInput,
  type TextInputNumberProps,
  type TextInputSearchProps,
  type TextInputStringProps,
} from 'components/TextInput/TextInput';
import type { FormikProps } from 'formik';
import type { RefObject } from 'react';
import type { ChangeEvent, FocusEvent, JSX, ReactNode } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import {
  type FormikAutoTrim,
  getTrimFunction,
} from 'utils/formik/formikAutoTrim';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';
import { useCheckNumberInputBoundaries } from 'utils/hooks/useCheckNumberInputBoundaries';

type Props<TFormikValues> = (
  | Omit<TextInputNumberProps, 'value' | 'onChange'>
  | Omit<TextInputSearchProps, 'value' | 'onChange'>
  | Omit<TextInputStringProps, 'value' | 'onChange'>
) & {
  autoComplete?: string;
  autoFocus?: boolean;
  autoTrim?: FormikAutoTrim;
  formik: FormikProps<TFormikValues>;
  inputRef?: RefObject<HTMLInputElement>;
  name: StringKey<TFormikValues> | NestedFormikName;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  renderError?: (error: string) => ReactNode | string;
  label?: ReactNode;
  description?: ReactNode;
  // In case of nullish value, renders the input as disabled with this message replacing the value. Without this prop set, nullish value is forbidden.
  // This is a workaround to efficiently handle specific cases, where value can be null or undefined, nevertheless it is preferred, what value is always provided.
  disableOnNullishValueMessage?: string;
};

export const FormikTextField = <TFormikValues,>({
  autoTrim,
  formik,
  inputRef,
  label,
  description,
  onChange,
  disableOnNullishValueMessage,
  disabled,
  renderError,
  ...props
}: Props<TFormikValues>): JSX.Element => {
  const { value, ...field } = formik.getFieldProps(props.name);
  const { error, initialValue } = formik.getFieldMeta(props.name as string);
  const hasError = fieldHasError<TFormikValues>(formik, props.name);

  const isNullishValue = isNullable(value);
  const fieldValue = value ?? disableOnNullishValueMessage;
  const isDisabled =
    disabled || (disableOnNullishValueMessage !== undefined && isNullishValue);

  // handle unsupported state for nullish value and undefined disableOnNullishValueMessage prop
  if (isNullishValue && disableOnNullishValueMessage === undefined) {
    stumble(
      'FormikTextField is in unsupported state. Provide disableOnNullishValueMessage when value is not defined.',
    );
  }

  useCheckNumberInputBoundaries({
    name: props.name,
    type: props.type,
    value: initialValue,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const trimFunction = getTrimFunction(autoTrim);

    trimFunction(field.onChange)(e);
    if (onChange) {
      trimFunction(onChange)(e);
    }
  };

  const handleBlur = () => {
    field.onBlur({
      target: {
        name: field.name,
      },
    });
  };

  return (
    <FormField
      className={classNames(hasError && 'formik-error-scroll-anchor')}
      label={label}
      description={description}
      error={hasError ? error : undefined}
      renderError={hasError && renderError ? renderError : undefined}
    >
      <TextInput
        {...field}
        {...props}
        value={fieldValue}
        disabled={isDisabled}
        hasError={hasError}
        inputRef={inputRef}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </FormField>
  );
};
